import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Tag, Percent, Euro, Plus, Save, Trash2, Power, PowerOff,
  Calendar, Users, Ticket, Sparkles, Eye,
} from "lucide-react";

const PLAN_KEYS = ["top_budget", "medium_budget", "standard_budget", "maximum_budget"];
const CYCLES = ["monthly", "annual"];
const PLAN_LABELS = {
  top_budget: "TOP Budget",
  medium_budget: "MEDIUM Budget",
  standard_budget: "STANDARD Budget",
  maximum_budget: "MAXIMUM Budget",
};

const STATUS_META = {
  running: { label: "En cours", bg: "#059669", fg: "#fff" },
  scheduled: { label: "Programmée", bg: "#002FA7", fg: "#fff" },
  expired: { label: "Terminée", bg: "#868E96", fg: "#fff" },
  exhausted: { label: "Épuisée", bg: "#E60000", fg: "#fff" },
  off: { label: "Désactivée", bg: "#0A0A0A", fg: "#ADB5BD" },
};

const EMPTY_FORM = {
  name: "", code: "", description: "",
  discount_type: "percent", discount_value: 15,
  plan_keys: [], cycle: "", auto_apply: true, active: true,
  starts_at: "", ends_at: "",
  max_uses: "", max_uses_per_user: "",
  visible_on_landing: true, priority: 0, cumulable: false,
};

const VALID_STATUS = Object.keys(STATUS_META);

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—"
    : d.toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const fmtDuration = (seconds) => {
  if (seconds == null) return "—";
  const s = Math.max(0, seconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d} j ${h} h`;
  if (h > 0) return `${h} h ${m} min`;
  return `${m} min`;
};

export default function AdminPromotions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null OR promo.id OR "new"
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [basePrices, setBasePrices] = useState({}); // v2.0.21 — vrais prix depuis /public/plans
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    api.get("/public/plans?version=v2").then(({ data }) => {
      const map = {};
      (data || []).forEach((p) => {
        map[`${p.key}_monthly`] = p.monthly_price ?? p.monthly_price_max ?? p.monthly_price_min ?? null;
        map[`${p.key}_annual`] = p.annual_price ?? p.annual_price_max ?? p.annual_price_min ?? null;
      });
      setBasePrices(map);
    }).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promotions");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing("new");
    setForm(EMPTY_FORM);
  };

  const startEdit = (promo) => {
    setEditing(promo.id);
    setForm({
      name: promo.name || "",
      code: promo.code || "",
      description: promo.description || "",
      discount_type: promo.discount_type || "percent",
      discount_value: promo.discount_value || 15,
      plan_keys: promo.plan_keys || [],
      cycle: promo.cycle || "",
      auto_apply: promo.auto_apply !== false,
      active: promo.active !== false,
      starts_at: (promo.starts_at || "").slice(0, 16),
      ends_at: (promo.ends_at || "").slice(0, 16),
      max_uses: promo.max_uses ?? "",
      max_uses_per_user: promo.max_uses_per_user ?? "",
      visible_on_landing: promo.visible_on_landing !== false,
      priority: promo.priority ?? 0,
      cumulable: !!promo.cumulable,
    });
  };

  const closeEdit = () => { setEditing(null); setForm(EMPTY_FORM); };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_value: Number(form.discount_value),
        plan_keys: form.plan_keys.length ? form.plan_keys : null,
        cycle: form.cycle || null,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        max_uses: form.max_uses === "" ? null : Number(form.max_uses),
        max_uses_per_user: form.max_uses_per_user === "" ? null : Number(form.max_uses_per_user),
        priority: Number(form.priority) || 0,
        cumulable: !!form.cumulable,
        code: form.code ? form.code.toUpperCase() : null,
      };
      if (editing === "new") {
        await api.post("/admin/promotions", payload);
        toast.success("Promotion créée");
      } else {
        await api.patch(`/admin/promotions/${editing}`, payload);
        toast.success("Promotion mise à jour");
      }
      closeEdit();
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const togglePromo = async (id) => {
    try {
      await api.post(`/admin/promotions/${id}/toggle`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deletePromo = async (id, name) => {
    if (!window.confirm(`Supprimer la promotion "${name}" ? Les inscriptions déjà tarifées ne sont PAS impactées.`)) return;
    try {
      await api.delete(`/admin/promotions/${id}`);
      toast.success("Promotion supprimée");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const togglePlanKey = (pk) => {
    setForm((f) => f.plan_keys.includes(pk)
      ? { ...f, plan_keys: f.plan_keys.filter(x => x !== pk) }
      : { ...f, plan_keys: [...f.plan_keys, pk] });
  };

  const previewPrices = useMemo(() => {
    // v2.0.21 — Les prix d'aperçu étaient codés en dur (24/66/82 · 290/790/990) et
    // devenaient faux dès qu'un tarif changeait. On lit désormais les vrais prix
    // renvoyés par le backend (champ `preview`, calculé sur la collection `plans`).
    const rows = [];
    const plans = form.plan_keys.length ? form.plan_keys : PLAN_KEYS;
    const cycles = form.cycle ? [form.cycle] : CYCLES;
    for (const p of plans) for (const c of cycles) {
      const base = basePrices[`${p}_${c}`];
      if (!base) continue; // maximum_budget "sur devis" n'a pas de prix ferme
      const val = Number(form.discount_value) || 0;
      const disc = form.discount_type === "percent"
        ? Math.min(base, Math.round(base * val) / 100)
        : Math.min(base, val);
      rows.push({ plan: p, cycle: c, base, discount: disc, final: Math.max(0, Math.round((base - disc) * 100) / 100) });
    }
    return rows;
  }, [form, basePrices]);

  const windowHint = useMemo(() => {
    const now = new Date();
    const s = form.starts_at ? new Date(form.starts_at) : null;
    const e = form.ends_at ? new Date(form.ends_at) : null;
    if (s && e && e <= s) return { tone: "error", text: "La date de fin doit être postérieure à la date de début." };
    if (!form.active) return { tone: "off", text: "Promotion désactivée : elle ne s'appliquera pas, quelles que soient les dates." };
    if (s && s > now) return { tone: "scheduled", text: `Programmée : démarrera le ${s.toLocaleString("fr-FR")}.` };
    if (e && e < now) return { tone: "expired", text: `Terminée depuis le ${e.toLocaleString("fr-FR")}.` };
    if (e) return { tone: "running", text: `En cours jusqu'au ${e.toLocaleString("fr-FR")}.` };
    return { tone: "running", text: "En cours, sans date de fin." };
  }, [form.starts_at, form.ends_at, form.active]);

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto space-y-6" data-testid="admin-promotions">
      <PageHeader
        title="Promotions & Offres"
        subtitle="Créez des remises (pourcentage ou montant fixe), automatiques ou via code promo, ciblées par plan et cycle."
      />

      {!editing && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* v2.0.21 — Filtre par statut réel (dates prises en compte) */}
          <div className="flex flex-wrap gap-1.5" data-testid="promo-status-filter">
            {["all", ...VALID_STATUS].map((st) => {
              const n = st === "all" ? items.length : items.filter((i) => i.status === st).length;
              return (
                <button key={st} type="button" onClick={() => setStatusFilter(st)}
                        data-testid={`promo-filter-${st}`}
                        className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 border transition ${
                          statusFilter === st ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#868E96] border-[#DEE2E6]"
                        }`}>
                  {st === "all" ? "Toutes" : STATUS_META[st].label} ({n})
                </button>
              );
            })}
          </div>
          <Button onClick={startCreate} className="bg-[#F59E0B] text-black font-bold" data-testid="promo-new">
            <Plus className="h-4 w-4 mr-1" />Nouvelle promotion
          </Button>
        </div>
      )}

      {/* Form panel */}
      {editing && (
        <div className="p-5 bg-white border-2 border-[#F59E0B]" data-testid="promo-form">
          <div className="font-display font-bold text-lg mb-4 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#F59E0B]" />
            {editing === "new" ? "Nouvelle promotion" : "Modifier la promotion"}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Nom * (interne)</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Rentrée 2026 -15%" data-testid="promo-form-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Code promo (majuscules · facultatif)</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="SUMMER15 (vide = auto uniquement)" data-testid="promo-form-code" />
              <div className="text-[10px] text-[#868E96] mt-1">Sans code : la remise s'applique automatiquement si `auto_apply` activé.</div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase tracking-wider">Description (affichée sur landing)</Label>
              <Textarea rows={2} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Offre de rentrée : -15% sur le plan Business en engagement annuel" data-testid="promo-form-desc" />
            </div>

            {/* Type + valeur */}
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Type de remise *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, discount_type: "percent" })}
                  data-testid="promo-type-percent"
                  className={`p-3 border-2 text-left transition ${form.discount_type === "percent" ? "border-[#F59E0B] bg-[#FFFDF2]" : "border-[#DEE2E6] bg-white hover:border-[#ADB5BD]"}`}
                >
                  <div className="flex items-center gap-2 font-bold text-sm"><Percent className="h-4 w-4" />Pourcentage</div>
                  <div className="text-[10px] text-[#495057]">ex: -15%</div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, discount_type: "fixed_amount" })}
                  data-testid="promo-type-fixed"
                  className={`p-3 border-2 text-left transition ${form.discount_type === "fixed_amount" ? "border-[#F59E0B] bg-[#FFFDF2]" : "border-[#DEE2E6] bg-white hover:border-[#ADB5BD]"}`}
                >
                  <div className="flex items-center gap-2 font-bold text-sm"><Euro className="h-4 w-4" />Montant fixe</div>
                  <div className="text-[10px] text-[#495057]">ex: -100€</div>
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">
                Valeur * ({form.discount_type === "percent" ? "%" : "€"})
              </Label>
              <Input type="number" min={0.01} step="0.01"
                max={form.discount_type === "percent" ? 100 : undefined}
                value={form.discount_value}
                onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                data-testid="promo-form-value" />
            </div>

            {/* Cible */}
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Plans ciblés (vide = tous)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {PLAN_KEYS.map(pk => (
                  <button
                    key={pk}
                    type="button"
                    onClick={() => togglePlanKey(pk)}
                    data-testid={`promo-plan-${pk}`}
                    className={`text-[11px] px-2.5 py-1 border font-bold ${form.plan_keys.includes(pk) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6]"}`}
                  >
                    {PLAN_LABELS[pk]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Cycle (vide = les deux)</Label>
              <div className="flex gap-2 mt-1">
                {["", "monthly", "annual"].map(cy => (
                  <button
                    key={cy || "both"}
                    type="button"
                    onClick={() => setForm({ ...form, cycle: cy })}
                    data-testid={`promo-cycle-${cy || "both"}`}
                    className={`text-[11px] px-3 py-1 border font-bold ${form.cycle === cy ? "bg-[#0070BA] text-white border-[#0070BA]" : "bg-white border-[#DEE2E6]"}`}
                  >
                    {cy === "" ? "Les deux" : cy === "monthly" ? "Mensuel" : "Annuel"}
                  </button>
                ))}
              </div>
            </div>

            {/* Fenêtre */}
            <div className="md:col-span-2 p-3 border-2 border-[#DEE2E6] bg-[#F8F9FA]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#495057] mb-2 flex items-center gap-1">
                <Calendar className="h-3 w-3" />Fenêtre d'application automatique
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Début (vide = immédiat)</Label>
                  <Input type="datetime-local" value={form.starts_at}
                    onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                    data-testid="promo-form-starts" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Fin (vide = sans limite)</Label>
                  <Input type="datetime-local" value={form.ends_at}
                    onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                    data-testid="promo-form-ends" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="text-[10px] uppercase tracking-wider text-[#868E96] font-bold">Raccourcis :</span>
                {[
                  { label: "7 jours", days: 7 },
                  { label: "14 jours", days: 14 },
                  { label: "30 jours", days: 30 },
                  { label: "Fin du mois", eom: true },
                ].map((sc) => (
                  <button key={sc.label} type="button" data-testid={`promo-window-${sc.days || "eom"}`}
                    onClick={() => {
                      const now = new Date();
                      const end = sc.eom
                        ? new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59)
                        : new Date(now.getTime() + sc.days * 86400000);
                      const fmt = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                      setForm((f) => ({ ...f, starts_at: fmt(now), ends_at: fmt(end) }));
                    }}
                    className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 border border-[#DEE2E6] bg-white hover:border-[#F59E0B]">
                    {sc.label}
                  </button>
                ))}
                {(form.starts_at || form.ends_at) && (
                  <button type="button" data-testid="promo-window-clear"
                    onClick={() => setForm((f) => ({ ...f, starts_at: "", ends_at: "" }))}
                    className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 border border-[#E60000] text-[#E60000] bg-white">
                    Effacer
                  </button>
                )}
              </div>
              <div className={`mt-2 text-[11px] font-semibold ${
                windowHint.tone === "error" ? "text-[#E60000]"
                  : windowHint.tone === "running" ? "text-[#059669]"
                  : windowHint.tone === "scheduled" ? "text-[#002FA7]" : "text-[#868E96]"
              }`} data-testid="promo-window-hint">
                {windowHint.text}
              </div>
            </div>

            {/* Limites */}
            <div>
              <Label className="text-[10px] uppercase tracking-wider flex items-center gap-1">
                <Users className="h-3 w-3" />Usages max (total · vide = ∞)
              </Label>
              <Input type="number" min={1} value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                placeholder="ex: 100" data-testid="promo-form-max-uses" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Usages max / utilisateur</Label>
              <Input type="number" min={1} value={form.max_uses_per_user}
                onChange={(e) => setForm({ ...form, max_uses_per_user: e.target.value })}
                placeholder="ex: 1" data-testid="promo-form-max-per-user" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Priorité (promos automatiques)</Label>
              <Input type="number" min={0} max={100} value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                data-testid="promo-form-priority" />
              <div className="text-[10px] text-[#868E96] mt-1">Si plusieurs promos auto s'appliquent, la priorité la plus haute gagne (à égalité : la plus grosse remise).</div>
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer pt-5">
              <input type="checkbox" checked={form.cumulable} onChange={(e) => setForm({ ...form, cumulable: e.target.checked })}
                data-testid="promo-form-cumulable" className="mt-0.5" />
              <span><b>Cumulable</b> — <span className="text-xs text-[#495057]">un code cumulable se cumule avec la meilleure promo auto cumulable (sinon : non cumulable, le code remplace la promo auto)</span></span>
            </label>

            {/* Toggles */}
            <div className="md:col-span-2 grid grid-cols-3 gap-3 pt-2 border-t border-[#DEE2E6]">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  data-testid="promo-form-active" />
                <span><b>Active</b> — Toggle général ON/OFF</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.auto_apply} onChange={(e) => setForm({ ...form, auto_apply: e.target.checked })}
                  data-testid="promo-form-auto" />
                <span><b>Auto-apply</b> — Sans code</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.visible_on_landing} onChange={(e) => setForm({ ...form, visible_on_landing: e.target.checked })}
                  data-testid="promo-form-landing" />
                <span><b>Landing</b> — Pastille visible</span>
              </label>
            </div>
          </div>

          {/* Preview prix */}
          {previewPrices.length > 0 && (
            <div className="mt-5 p-3 bg-[#F8F9FA] border border-[#DEE2E6]">
              <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-wider mb-2">
                <Eye className="h-3 w-3" />Aperçu des prix
              </div>
              <div className="grid md:grid-cols-2 gap-1 text-xs">
                {previewPrices.map((p, i) => (
                  <div key={i} className="flex justify-between p-1.5 bg-white border border-[#DEE2E6]" data-testid={`promo-preview-${p.plan}-${p.cycle}`}>
                    <span>{PLAN_LABELS[p.plan]} · {p.cycle === "monthly" ? "mens." : "annuel"}</span>
                    <span>
                      <s className="text-[#868E96]">{p.base}€</s>{" "}
                      <b className="text-[#059669]">{p.final}€</b>{" "}
                      <span className="text-[10px] text-[#F59E0B]">(-{p.discount}€)</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 flex gap-2 justify-end">
            <Button variant="outline" onClick={closeEdit} data-testid="promo-cancel">Annuler</Button>
            <Button onClick={submit} disabled={saving || !form.name || !form.discount_value}
              className="bg-[#F59E0B] text-black font-bold" data-testid="promo-submit">
              <Save className="h-4 w-4 mr-1" />{saving ? "Enregistrement…" : (editing === "new" ? "Créer" : "Mettre à jour")}
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      {!editing && (
        <div className="space-y-3">
          {loading && <div className="text-sm text-[#868E96]">Chargement…</div>}
          {!loading && items.length === 0 && (
            <div className="p-12 bg-white border border-dashed border-[#DEE2E6] text-center text-[#868E96]">
              <Sparkles className="h-8 w-8 mx-auto mb-2 text-[#F59E0B]" />
              Aucune promotion pour le moment. Créez-en une pour commencer !
            </div>
          )}
          {items.filter((p) => statusFilter === "all" || p.status === statusFilter).map(p => (
            <div key={p.id}
                 data-testid={`promo-row-${p.id}`}
                 className={`p-4 bg-white border-l-4 border border-[#DEE2E6] flex items-start gap-3 ${p.status === "running" ? "border-l-[#059669]" : p.status === "scheduled" ? "border-l-[#002FA7]" : "border-l-[#868E96] opacity-70"}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-bold text-base">{p.name}</div>
                  {p.code && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#0A0A0A] text-[#FFD700] px-2 py-0.5">
                      <Ticket className="h-3 w-3" />{p.code}
                    </span>
                  )}
                  {p.auto_apply && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#F59E0B] text-black px-2 py-0.5">Auto</span>
                  )}
                  {p.priority > 0 && (
                    <span className="text-[10px] font-bold uppercase tracking-wider border border-[#0A0A0A] px-2 py-0.5" data-testid={`promo-priority-${p.id}`}>Prio {p.priority}</span>
                  )}
                  {p.cumulable && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#002FA7] text-white px-2 py-0.5" data-testid={`promo-cumulable-${p.id}`}>Cumulable</span>
                  )}
                  {(() => {
                    const st = STATUS_META[p.status] || STATUS_META.running;
                    return (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
                            style={{ backgroundColor: st.bg, color: st.fg }}
                            data-testid={`promo-status-${p.id}`}>
                        {st.label}
                      </span>
                    );
                  })()}
                  {p.visible_on_landing === false && (
                    <span className="text-[10px] font-bold uppercase tracking-wider border border-[#DEE2E6] text-[#868E96] px-2 py-0.5"
                          data-testid={`promo-hidden-${p.id}`}>
                      Non affichée
                    </span>
                  )}
                </div>
                <div className="text-lg font-bold mt-1 text-[#F59E0B]">
                  {p.discount_type === "percent" ? `-${p.discount_value}%` : `-${p.discount_value}€`}
                  <span className="text-xs font-normal text-[#495057] ml-2">
                    {p.plan_keys.length ? p.plan_keys.map(pk => PLAN_LABELS[pk]).join(" · ") : "Tous les plans"}
                    {p.cycle ? ` · ${p.cycle === "monthly" ? "Mensuel" : "Annuel"}` : " · Les deux cycles"}
                  </span>
                </div>
                {p.description && <div className="text-sm text-[#495057] mt-1">{p.description}</div>}
                <div className="text-[10px] text-[#868E96] mt-2 flex gap-3 flex-wrap items-center">
                  {p.starts_at || p.ends_at ? (
                    <span data-testid={`promo-window-${p.id}`}>
                      {p.starts_at && p.ends_at
                        ? <>Du <b>{fmtDate(p.starts_at)}</b> au <b>{fmtDate(p.ends_at)}</b></>
                        : p.ends_at
                          ? <>Jusqu'au <b>{fmtDate(p.ends_at)}</b></>
                          : <>À partir du <b>{fmtDate(p.starts_at)}</b></>}
                    </span>
                  ) : (
                    <span>Aucune date — s'applique en continu</span>
                  )}
                  {p.status === "running" && p.ends_in_seconds != null && (
                    <span className="text-[#F59E0B] font-bold" data-testid={`promo-remaining-${p.id}`}>
                      Plus que {fmtDuration(p.ends_in_seconds)}
                    </span>
                  )}
                  {p.status === "scheduled" && p.starts_in_seconds != null && (
                    <span className="text-[#002FA7] font-bold" data-testid={`promo-startsin-${p.id}`}>
                      Démarre dans {fmtDuration(p.starts_in_seconds)}
                    </span>
                  )}
                  <span>Usages : <b>{p.uses_count}</b>{p.max_uses ? ` / ${p.max_uses}` : ""}</span>
                  {p.max_uses_per_user && <span>Max <b>{p.max_uses_per_user}</b>/client</span>}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <Button size="sm" variant="outline" onClick={() => startEdit(p)} data-testid={`promo-edit-${p.id}`}>
                  Modifier
                </Button>
                <Button size="sm" variant="outline" onClick={() => togglePromo(p.id)} data-testid={`promo-toggle-${p.id}`}
                        className={p.active ? "text-[#F59E0B] border-[#F59E0B]" : "text-[#059669] border-[#059669]"}>
                  {p.active ? <><PowerOff className="h-3.5 w-3.5 mr-1" />OFF</> : <><Power className="h-3.5 w-3.5 mr-1" />ON</>}
                </Button>
                <Button size="sm" variant="outline" onClick={() => deletePromo(p.id, p.name)}
                        className="text-[#E60000] border-[#E60000]" data-testid={`promo-delete-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
